import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useMemo,
} from "react";

export interface StudyContextData {
  patientName?: string;
  patientMRN?: string;
  patientDateOfBirth?: string;
  studyId?: string;
  recordingId?: string;
  /** Internal recording UUID — fallback when EDF exam id is a placeholder. */
  recordingTechnicalId?: string;
  recordingDate?: string;
  recordingTime?: string;
  studyType?: string;
}

interface StudyContextType {
  studyData: StudyContextData | null;
  setStudyData: (data: StudyContextData | null) => void;
  clearStudyData: () => void;
}

const StudyContext = createContext<StudyContextType | undefined>(undefined);

export const StudyProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [studyData, setStudyDataState] = useState<StudyContextData | null>(null);

  const setStudyData = useCallback((next: StudyContextData | null) => {
    setStudyDataState((current) => {
      if (current === next) return current;
      if (!current && !next) return current;
      if (!current || !next) return next;

      const unchanged =
        current.patientName === next.patientName &&
        current.patientMRN === next.patientMRN &&
        current.patientDateOfBirth === next.patientDateOfBirth &&
        current.studyId === next.studyId &&
        current.recordingId === next.recordingId &&
        current.recordingTechnicalId === next.recordingTechnicalId &&
        current.recordingDate === next.recordingDate &&
        current.recordingTime === next.recordingTime &&
        current.studyType === next.studyType;

      return unchanged ? current : next;
    });
  }, []);

  const clearStudyData = useCallback(() => {
    setStudyDataState((current) => (current ? null : current));
  }, []);

  const value = useMemo(
    () => ({ studyData, setStudyData, clearStudyData }),
    [studyData, setStudyData, clearStudyData],
  );

  return (
    <StudyContext.Provider value={value}>
      {children}
    </StudyContext.Provider>
  );
};

export const useStudy = () => {
  const context = useContext(StudyContext);
  if (context === undefined) {
    throw new Error("useStudy must be used within a StudyProvider");
  }
  return context;
};

/** Safe for shared chrome (Header) when ClinicalProviders omit StudyProvider (e.g. /worklist LCP). */
export const useOptionalStudy = (): StudyContextType => {
  const context = useContext(StudyContext);
  return (
    context ?? {
      studyData: null,
      setStudyData: () => undefined,
      clearStudyData: () => undefined,
    }
  );
};
